// @flow

import React, { useEffect } from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { formatAuthoredBy } from "@transformers/names";
import { getRouteParams, getCurrentPath } from "@selectors";
import { Page } from "react-page-layout";
import { View, Text, Loader } from "@components";
import { getEmployee, isLoading } from "@modules/employee/selectors";
import { isLoading as isSharedLoading } from "@modules/shared/selectors";
import actionCreators from "@modules/employee/actionCreator";
import { EventHistory } from "@modules/shared/components";
import type { EmployeeType } from "@modules/employee/types";
import withErrorHandler from "@components/error";
import { withClientContext, type PropsFromClientContext } from "@modules/context/components/";
import {
  withPermissions,
  PermissionEnum,
  type PropsFromPermissions,
} from "@modules/account/components";
import { isFirstCareUser } from "@modules/account/selectors";
import * as clientsSelectors from "@modules/client/selectors";
import type { ClientServiceAbsenceManagement } from "@modules/client/reducer/service/types";

type PropsFromState = $Exact<{
  isLoading?: boolean,
  currentPath: string,
  employeeId: number,
  employee: EmployeeType,
  isFirstCareUser: boolean,
  service: ClientServiceAbsenceManagement,
}>;

type PropsFromDispatch = $Exact<{
  getEmployees: typeof actionCreators.getEmployees,
}>;

type Props = $Exact<{
  ...PropsFromPermissions,
  ...PropsFromClientContext,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export function EmployeeMonitoring(props: Props) {
  const isAbsenceMonitoringEnabled = props.service?.settings?.absenceMonitoring;
  const message = isAbsenceMonitoringEnabled
    ? "EMPLOYEE_MONITORING_TAB_INFO"
    : "EMPLOYEE_MONITORING_TAB_NO_SERVICE";

  useEffect(() => {
    props.getEmployees({
      clientId: props.clientId,
      filters: {
        id: props.employeeId,
      },
    });
  }, []);

  function handleGuideLoad() {
    window.open(
      // TODO: remove inline functions
      "/assets/pdf/M2.2.3.5_Guide_to_Event_History_in_Insight_v1_30.09.2019.pdf",
      "_blank",
    );
  }

  return (
    <View position="relative" gap={8}>
      <Text className="message-box" text={props.t(message)} />

      {props.isLoading && <Loader />}
      {isAbsenceMonitoringEnabled && (
        <View gap={6}>
          <EventHistory
            clientId={props.clientId}
            employeeId={props.employeeId}
            showSummary
            enableMonitoringUpdate
            defaultFilter={"ABSENCE_MONITORING_REVIEW"}
            filters={["ABSENCE_MONITORING_REVIEW"]}
            gridSectionTitle={props.t("ABSENCE_MONITORING_HISTORY")}
            enableAppeals
            useAbsenceReviewColumns
          />
          <EventHistory
            clientId={props.clientId}
            employeeId={props.employeeId}
            enableMonitoringUpdate
            defaultFilter={"ALL"}
            filters={[
              "ALL",
              "RTW_INTERVIEW",
              "ALERT_TRIGGERED",
              "REFERRALS",
              "ABSENCE_MONITORING_REVIEW",
            ]}
            gridSectionTitle={props.t("ACTIVITY_HISTORY")}
          />
        </View>
      )}
    </View>
  );
}

const mapStateToProps = (state: Object, ownProps): PropsFromState => {
  const routeParams = getRouteParams(ownProps);
  const employeeId = parseInt(routeParams.employeeId);

  return {
    isLoading: isLoading(state) || isSharedLoading(state),
    currentPath: getCurrentPath(state),
    employee: getEmployee(employeeId)(state),
    employeeId,
    isFirstCareUser: isFirstCareUser(state),
    service: clientsSelectors.getService(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getEmployees: actionCreators.getEmployees,
};

export default compose(
  withErrorHandler(),
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
  withClientContext(),
  withPermissions({
    permissionId: PermissionEnum.ACCESS_REPORTING_EMPLOYEES,
  }),
)(EmployeeMonitoring);
